import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import cogoToast from '@successtar/cogo-toast'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import type { PrintPreviewRef } from '@renderer/components/PrintPreview'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import type { PhotoState } from '@renderer/utils/generateFramedVideo'
import { printSessionStrip } from '@renderer/utils/pipeline/sessionPrint'
import { editShouldUploadPhotos } from '@renderer/utils/pipeline/photoUploadOwnership'
import {
  processAndUploadPhotos,
  processAndUploadStrip,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { isFramedStripRecording } from '@renderer/utils/recordingType'
import { resolveFramedVideoPlaybackAndRemote } from '@renderer/utils/galleryUrlUtils'
import { waitPrintResultMinDisplay } from '@renderer/utils/printResultMinDisplay'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'
import { bakeStickers, type StickerPlacement } from '@renderer/lib/stickers/bakeStickers'
import type { BrushStroke } from '@renderer/lib/stickers/brush'

interface UserContext {
  user?: { email: string }
}

/** Past this, the guest is sent on whatever is still running. */
const PROCESSING_CAP_MS = 30000

/**
 * The sticker page's Continue: bake, print, upload, move on.
 *
 * With the Edit page on, Select hands printing and the framed video to this
 * page (see Select's "deferring printing and video processing"), so this is
 * where the guest's print is sent. It is the original editor's Continue step
 * for step — same print condition, same uploads, same edited-strip gallery
 * item, same video task, same minimum print-result dwell, same 30s safety net
 * — with the composite done by the shared print bake, which draws the
 * percentage placements every preview uses.
 */
export function useFinishEdit(previewRef: React.RefObject<PrintPreviewRef>): {
  finish: (stripUrl: string, placements: StickerPlacement[], strokes?: BrushStroke[]) => Promise<void>
  isProcessing: boolean
} {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const { setSessionState, logActivity } = useSessionStateStore()
  const { downloadLocal } = useSettingsStore()
  const [isProcessing, setIsProcessing] = useState(false)
  const processingRef = useRef(false)
  const printJobSentRef = useRef(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return (): void => {
      mountedRef.current = false
    }
  }, [])

  const goOn = useCallback((): void => {
    if (mountedRef.current) {
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    }
  }, [navigate, projectId])

  const stripBlobFrom = useCallback(
    async (stripUrl: string): Promise<Blob> => {
      if (stripUrl) {
        try {
          const res = await fetch(stripUrl)
          if (res.ok) return await res.blob()
        } catch (error) {
          console.warn('[Stickers] strip URL unreadable, regenerating:', error)
        }
      }
      // No strip, or a blob URL that no longer resolves: render it again.
      const regenerated = await previewRef.current?.exportImage()
      if (!regenerated) throw new Error('No strip available and it could not be regenerated')
      return regenerated
    },
    [previewRef]
  )

  const finish = useCallback(
    async (
      stripUrl: string,
      placements: StickerPlacement[],
      strokes: BrushStroke[] = []
    ): Promise<void> => {
      if (processingRef.current || !mountedRef.current) return
      processingRef.current = true
      setIsProcessing(true)
      const startedAt = Date.now()
      let didAttemptPrint = false

      let capFired = false
      const cap = setTimeout(() => {
        capFired = true
        console.warn('[Stickers] Processing cap reached, moving on')
        if (mountedRef.current) setIsProcessing(false)
        stateUploader.uploadSessionState().catch(() => {})
        goOn()
      }, PROCESSING_CAP_MS)

      const { userPreferences, gallery, galleryKey, id } = useSessionStateStore.getState()
      const globalConfig = useConfigStore.getState().config
      const { getFrame, frames } = useConfigStore.getState()

      try {
        const finalBlob = await bakeStickers(await stripBlobFrom(stripUrl), placements, strokes)
        logActivity('Sticker page: strip finished', {
          stickers: placements.length,
          strokes: strokes.length
        })

        let isOnline = true
        const bridge = window as typeof window & {
          api?: { getInternetStatus?: () => Promise<boolean> }
        }
        isOnline = bridge.api?.getInternetStatus
          ? await bridge.api.getInternetStatus()
          : navigator.onLine

        // Select deferred the session's print to this page. The shared step
        // routes it (parallel round-robin or frame), tags it with the session,
        // skips digital booths and sends it once.
        didAttemptPrint = await printSessionStrip({
          page: 'edit',
          stripBlob: finalBlob,
          usePrint: userPreferences.usePrint,
          copies: userPreferences.copies,
          frameId: userPreferences.frameId || '',
          isOnline,
          context: { sessionId: id || undefined, projectId: projectId || undefined },
          sentRef: printJobSentRef,
          logActivity,
          t
        })

        const currentGallery = useSessionStateStore.getState().gallery
        const watermarkUrl = globalConfig?.pages.capture?.settings?.watermarkUrl
        const ctx: ProcessingContext = {
          context,
          projectId,
          sessionId: id || '',
          galleryId: galleryKey || gallery.id,
          downloadLocal,
          isOnline
        }
        const capture = globalConfig?.pages.capture?.settings
        const watermarkConfig = watermarkUrl?.trim()
          ? {
              watermarkUrl,
              watermarkHorizontalAnchor: capture?.watermarkHorizontalAnchor || 'left',
              watermarkVerticalAnchor: capture?.watermarkVerticalAnchor || 'top',
              watermarkHorizontalOffset: capture?.watermarkHorizontalOffset || 0,
              watermarkVerticalOffset: capture?.watermarkVerticalOffset || 0
            }
          : undefined

        if (editShouldUploadPhotos(currentGallery, Boolean(watermarkConfig))) {
          // captureIndex matches the upload filename (`photo-${index}.jpg`), so
          // PostSessionAI can map server outputs back to the right gallery item.
          const forUpload = currentGallery.capturedItems.filter(
            (item) => item.photoUrl && item.photoUrl.trim() !== ''
          )
          const indexById = new Map<string, number>()
          forUpload.forEach((item, idx) => {
            if (item.id) indexById.set(item.id, idx)
          })
          if (indexById.size > 0) {
            const g = useSessionStateStore.getState().gallery
            setSessionState({
              gallery: {
                ...g,
                capturedItems: g.capturedItems.map((it) => {
                  const idx = indexById.get(it.id)
                  return idx === undefined ? it : { ...it, captureIndex: idx }
                })
              }
            })
          }
          processAndUploadPhotos(currentGallery.capturedItems, ctx, watermarkConfig, () => {
            if (mountedRef.current) {
              cogoToast.error(t('edit.errorUploadingPhotos', 'Error uploading individual photos'))
            }
          })
            .then(() => {
              if (!mountedRef.current) return
              const g = useSessionStateStore.getState().gallery
              setSessionState({
                gallery: {
                  ...g,
                  individualPhotosUploaded: true,
                  individualPhotosUploadedAt: Date.now()
                }
              })
            })
            .catch((err: unknown) =>
              console.warn('[Stickers] processAndUploadPhotos (non-blocking):', err)
            )
        }

        // Upload the finished strip, but never hold the guest more than 10s for it.
        const uploaded = await Promise.race([
          processAndUploadStrip(finalBlob, ctx).then((r) =>
            r ? { localUrl: r.localUrl } : { localUrl: URL.createObjectURL(finalBlob) }
          ),
          new Promise<{ localUrl: string }>((resolve) =>
            setTimeout(() => resolve({ localUrl: URL.createObjectURL(finalBlob) }), 10000)
          )
        ])

        // Re-read: the upload step above wrote captureIndex onto these items.
        const withoutEdited = useSessionStateStore.getState().gallery.capturedItems.filter(
          (item) => !item.id?.includes('dreambooth-strip-with-stickers')
        )
        setSessionState({
          gallery: {
            capturedItems: [
              ...withoutEdited,
              {
                id: `dreambooth-strip-with-stickers-${id}`,
                stripUrl: uploaded.localUrl || stripUrl,
                photoUrl: '',
                timestamp: new Date()
              }
            ],
            stripUrl: uploaded.localUrl || stripUrl
          }
        })

        if (userPreferences.useRecording && isFramedStripRecording(userPreferences.recordingType)) {
          const photosForVideo = gallery.capturedItems
            .filter((item) => item.photoUrl && !item.id?.includes('dreambooth-strip'))
            .map((item) => item.photoUrl!)
          const resources = photosForVideo.map((photoUrl) => ({
            photoUrl,
            videoUrl:
              gallery.capturedItems.find((item) => item.photoUrl === photoUrl)?.recordingUrl || ''
          }))
          const photoStates: Record<string, PhotoState> =
            useSessionStateStore.getState().gallery.photoStates ||
            previewRef.current?.getPhotoStates() ||
            {}
          const taskId = `video-${userPreferences.recordingType}-final-${id}-${Date.now()}`
          const unscaled = getFrame(userPreferences.frameId || frames[0]?._id, 1)
          const selected = getFrame(userPreferences.frameId || frames[0]?._id)

          backgroundVideoProcessor.processVideo({
            id: taskId,
            type: userPreferences.recordingType === 'strip_story' ? 'story' : 'live',
            resources,
            frameConfig: unscaled
              ? { id: unscaled._id, baseImage: unscaled.thumbnail, drawParams: unscaled.drawParams }
              : {
                  id: selected?._id || '',
                  baseImage: selected?.thumbnail || '',
                  drawParams: selected?.drawParams || {
                    canvasWidth: 1200,
                    canvasHeight: 1800,
                    backgroundColor: '#FFFFFF',
                    images: []
                  }
                },
            sessionId: id || '',
            userId: context?.user?.email || '',
            projectId: projectId || '',
            galleryId: galleryKey || gallery.id,
            speed: capture?.recordingSpeed || 1,
            fps: 30,
            photoStates,
            countdown: capture?.captureCountdown || 1000,
            onComplete: (uploadedUrl, localUrl) => {
              const { playbackUrl, videoRemoteUrl } = resolveFramedVideoPlaybackAndRemote(
                uploadedUrl,
                localUrl
              )
              if (!playbackUrl) return
              const prev = useSessionStateStore
                .getState()
                .gallery.capturedItems.filter((i) => i.id !== taskId)
              setSessionState({
                gallery: {
                  capturedItems: [
                    ...prev,
                    {
                      id: taskId,
                      videoUrl: playbackUrl,
                      videoRemoteUrl,
                      photoUrl: '',
                      timestamp: new Date()
                    }
                  ]
                }
              })
              logActivity('Framed strip video ready (edited)', {
                taskId,
                hasRemoteUrl: Boolean(uploadedUrl && uploadedUrl.length > 0)
              })
            },
            onError: (error) => {
              const message = error instanceof Error ? error.message : 'Unknown error'
              logActivity(`Video processing failed: ${message}`, {
                taskId,
                recordingType: userPreferences.recordingType,
                photosCount: photosForVideo.length,
                error: message,
                level: 'error'
              })
              if (mountedRef.current) cogoToast.error('Error processing video with stickers')
            }
          })
        }

        stateUploader.uploadSessionState().catch(() => {})
        clearTimeout(cap)
        if (capFired) return

        const minDisplaySec = useSettingsStore.getState().printResultMinDisplaySeconds
        if (didAttemptPrint && minDisplaySec > 0) {
          await waitPrintResultMinDisplay(startedAt, minDisplaySec, {
            isStillMounted: () => mountedRef.current
          })
        }
        if (mountedRef.current) setIsProcessing(false)
        await new Promise((resolve) => setTimeout(resolve, 100))
        goOn()
      } catch (error) {
        console.error('[Stickers] finish failed:', error)
        logActivity(
          `Sticker page finish failed: ${error instanceof Error ? error.message : String(error)}`,
          {
            level: 'error'
          }
        )
        cogoToast.error('Failed to process strip with stickers')
        clearTimeout(cap)
        if (capFired) return
        if (mountedRef.current) setIsProcessing(false)
        stateUploader.uploadSessionState().catch(() => {})
        await new Promise((resolve) => setTimeout(resolve, 100))
        goOn()
      } finally {
        processingRef.current = false
      }
    },
    [
      context,
      downloadLocal,
      goOn,
      logActivity,
      previewRef,
      projectId,
      setSessionState,
      stripBlobFrom,
      t
    ]
  )

  return { finish, isProcessing }
}
